import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Button,
  Dialog,
  DialogContent,
  Grid,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Select } from "antd";

import Loading from "../../../common-components/Loading";
import ActionMessages from "../../../common-components/ActionMessages";
import { updateClinLevelDetailsAPI } from "../../../api/financial-management-api";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "23px",
  },
  editBtn: {
    border: "1px solid #0070D2",
    borderRadius: "20px",
    padding: "2px 8px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    float: "right",
  },
  cancelBtn: {
    border: "1px solid #0070D2",
    borderRadius: "20px",
    padding: "4px 20px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
  },
  saveBtn: {
    border: "1px solid #0070D2",
    borderRadius: "20px",
    padding: "4px 20px",
    cursor: "pointer",
    marginRight: "8px",
    display: "inline-flex",
    alignItems: "center",
  },
  editIcon: {
    color: "#0070D2",
    fontSize: "14px",
    marginTop: "2px",
  },
  editText: {
    color: "#0070D2",
    marginLeft: "4px",
    fontWeight: 600,
    fontSize: "13px",
  },
  tableTitle: {
    width: "220px",
    height: "36px",
    padding: "0 0 0 12px",
    fontSize: "15px",
    color: "#ffffff",
    backgroundColor: "#000",
    borderRadius: "0 10px 0 0",
    display: "flex",
    alignItems: "center",
  },
  quillCell: {
    "& .ql-editor": {
      minHeight: "80px",
      fontSize: "13px",
    },
    "& .ql-toolbar": {
      padding: "2px 4px",
    },
    "& .ql-container": {
      fontSize: "13px",
    },
  },
  quillPopup: {
    "& .ql-editor": {
      minHeight: "100px",
      fontSize: "13px",
    },
    "& .ql-container": {
      fontSize: "13px",
    },
  },
  contentDisplay: {
    minHeight: "24px",
    fontSize: "13px",
    "& p": {
      margin: 0,
    },
  },
  // Popup styles — same as HeatMap dialog in Index.js
  addDialog: {
    width: "860px",
    height: "auto",
    position: "relative",
  },
  popupHeader: {
    marginTop: "-20px",
    marginLeft: "-24px",
    marginRight: "-24px",
    background: "#4b5558",
  },
  closeIconAdd: {
    display: "inline-block",
    color: "black",
    background: "#ffff",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    textAlign: "center",
    lineHeight: "20px",
    cursor: "pointer",
    position: "absolute",
    top: "6px",
    right: "8px",
    fontWeight: 600,
    fontSize: "12px",
  },
  addColumnTitle: {
    height: "28px",
    textAlign: "center",
    marginBottom: "11px",
    color: "#fff",
    fontWeight: 600,
    fontSize: "14px",
    paddingTop: "6px",
  },
  fieldLabel: {
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "4px",
    marginTop: "12px",
  },
  popupFooter: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    gap: "8px",
  },
  addBtnPopup: {
    fontSize: "12px",
    padding: "4px 8px !important",
    textTransform: "none",
    background: "#0176D3",
    marginRight: "8px",
  },
  cancelBtnPopup: {
    fontSize: "12px",
    padding: "3px 8px !important",
    textTransform: "none",
    border: "0.5px solid #0176D3",
    color: "#0176D3",
  },
}));

const cols = [
  "headwinds",
  "tailwinds",
  "whatsInForecast",
  "varToBudget",
  "varToForecast",
  "notes",
];

const colLabels = {
  headwinds: "Headwinds",
  tailwinds: "Tailwinds",
  whatsInForecast: "What is in Forecast",
  varToBudget: "Variance to Budget",
  varToForecast: "Variance to Forecast",
  notes: "Notes",
};

const toLabel = (key) =>
  colLabels[key] ??
  key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

const QuillCell = React.memo(({ cellKey, value, onChange, className }) => (
  <div className={className}>
    <ReactQuill key={cellKey} value={value ?? ""} onChange={onChange} />
  </div>
));

const ClinDetailsTable = ({
  clinDetailsData,
  selectedContract,
  version,
  getVersionStatus,
  clins = [],
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [emptyErrMsg, setEmptyErrMsg] = useState(false);
  const [msgType, setMsgType] = useState();
  const [msgText, setMsgText] = useState();
  const [originalData, setOriginalData] = useState([]);
  const [data, setData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (clinDetailsData) {
      const cleanedData = clinDetailsData.map((row) =>
        cols.reduce((acc, key) => ({ ...acc, [key]: row[key] }), {
          id: row.id,
          clin: row.clin,
        }),
      );
      setData(cleanedData);
      setOriginalData(cleanedData);
    }
  }, [clinDetailsData]);

  const isClosed = (val) => {
    setEmptyErrMsg(!val);
  };

  const handleClickEdit = () => {
    setIsEdit(true);
  };

  const onCancelClick = () => {
    setData(originalData);
    setIsEdit(false);
  };

  const onSaveClick = () => {
    setLoading(true);
    updateClinLevelDetailsAPI(data, selectedContract, version).then((res) => {
      if (res.status === 200) {
        setIsEdit(false);
        setLoading(false);
        setOriginalData(data);
        setEmptyErrMsg(true);
        setMsgType("success");
        setMsgText("Data updated successfully");
      }
      if (res.status === 400) {
        setLoading(false);
        setEmptyErrMsg(true);
        setMsgType("error");
        setMsgText(res.data.message);
      }
    });
  };

  const onChangeField = (id, key, value) => {
    const changedData = JSON.parse(JSON.stringify(data));
    changedData.forEach((row) => {
      if (row.id === id) row[key] = value;
    });
    setData(changedData);
  };

  const handleOpenAdd = () => {
    const emptyRow = cols.reduce((acc, key) => ({ ...acc, [key]: "" }), {
      id: 0,
      clin: "",
    });
    setFormData(emptyRow);
    setShowAddDialog(true);
  };

  const handleCloseAdd = () => {
    setShowAddDialog(false);
  };

  const onChangeFormField = (key, value) => {
    const changedData = JSON.parse(JSON.stringify(formData));
    changedData[key] = value;
    setFormData(changedData);
  };

  const onAddRow = () => {
    setLoading(true);
    const updatedData = [...data, formData];
    updateClinLevelDetailsAPI(updatedData, selectedContract, version).then(
      (res) => {
        if (res.status === 200) {
          setData(updatedData);
          setOriginalData(updatedData);
          setShowAddDialog(false);
          setLoading(false);
          setEmptyErrMsg(true);
          setMsgType("success");
          setMsgText("Data added successfully");
        }
        if (res.status === 400) {
          setLoading(false);
          setEmptyErrMsg(true);
          setMsgType("error");
          setMsgText(res.message);
        }
      },
    );
  };

  const renderViewCell = (item, col) => (
    <div
      className={classes.contentDisplay}
      dangerouslySetInnerHTML={{ __html: item[col] || "" }}
    />
  );

  return (
    <>
      <div style={{ float: "right", margin: "6px  6px 6px 0" }}>
        {getVersionStatus.toLowerCase() !== "closed" &&
          (!isEdit ? (
            <>
              <div className={classes.editBtn} onClick={handleClickEdit}>
                <EditIcon className={classes.editIcon} />
                <span className={classes.editText}>Edit</span>
              </div>
              <div
                className={classes.editBtn}
                onClick={handleOpenAdd}
                style={{ marginRight: "8px" }}
              >
                <span className={classes.editText}>Add</span>
              </div>
            </>
          ) : (
            <>
              <span className={classes.saveBtn} onClick={onSaveClick}>
                <span className={classes.editText}>Save</span>
              </span>
              <span className={classes.cancelBtn} onClick={onCancelClick}>
                <span className={classes.editText}>Cancel</span>
              </span>
            </>
          ))}
      </div>

      <table cellPadding={0} cellSpacing={0} className="heatmapTable">
        <thead>
          <tr>
            <td>Clin</td>
            {cols.map((key) => (
              <td key={key}>{toLabel(key)}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          {data && data.length > 0 ? (
            data.map((item) => (
              <tr key={item.id}>
                <td style={{ verticalAlign: "top", paddingTop: "8px" }}>
                  {item.clin}
                </td>
                {cols.map((key) => (
                  <td key={key} style={{ verticalAlign: "top" }}>
                    {isEdit ? (
                      <QuillCell
                        cellKey={`${item.id}-${key}`}
                        className={classes.quillCell}
                        value={item[key]}
                        onChange={(value) => onChangeField(item.id, key, value)}
                      />
                    ) : (
                      renderViewCell(item, key)
                    )}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={cols.length + 1}
                style={{ textAlign: "center", fontWeight: "600" }}
              >
                No Data
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Add New Dialog */}
      {showAddDialog && (
        <Dialog
          disableEscapeKeyDown
          maxWidth="lg"
          aria-labelledby="add-clin-details-dialog"
          open={showAddDialog}
          onClose={(_, reason) => {
            if (reason !== "backdropClick") handleCloseAdd();
          }}
        >
          <DialogContent className={classes.addDialog}>
            <div className={classes.popupHeader}>
              <span className={classes.closeIconAdd} onClick={handleCloseAdd}>
                x
              </span>
              <div className={classes.addColumnTitle}>Add New</div>
            </div>

            <Grid container spacing={2}>
              <Grid item lg={12}>
                <div className={classes.fieldLabel}>
                  Select CLIN{" "}
                  <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </div>
                <Select
                  showSearch
                  mode="single"
                  style={{ width: "100%", zIndex: 9, marginBottom: "12px" }}
                  placeholder="Search to Select"
                  showArrow
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label.toLowerCase() ?? "").includes(
                      input.toLowerCase(),
                    )
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={clins.map((val) => ({ value: val, label: val }))}
                  onChange={(val) => onChangeFormField("clin", val)}
                  value={formData.clin}
                />
              </Grid>

              {cols.map((key) => (
                <Grid item lg={6} key={key}>
                  <div className={classes.fieldLabel}>{toLabel(key)}</div>
                  <div className={classes.quillPopup}>
                    <ReactQuill
                      value={formData[key]}
                      onChange={(value) => onChangeFormField(key, value)}
                    />
                  </div>
                </Grid>
              ))}
            </Grid>

            <div className={classes.popupFooter}>
              <Button
                variant="contained"
                className={classes.addBtnPopup}
                color="primary"
                onClick={onAddRow}
                disabled={!formData.clin.trim()}
              >
                Add
              </Button>
              <Button
                variant="outlined"
                className={classes.cancelBtnPopup}
                color="primary"
                onClick={handleCloseAdd}
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {emptyErrMsg && (
        <ActionMessages type={msgType} isClosed={isClosed} message={msgText} />
      )}
      {loading && <Loading />}
    </>
  );
};

export default ClinDetailsTable;
